import { useCallback, useEffect, useReducer, useState } from 'react'

type ErrorResult = {
  error: Error
  message: string
}
type FetchResult<T = unknown> = ErrorResult | T

interface QueryProps<FetchResponse, FetchArgument> {
  queryMethod: (arg?: FetchArgument) => Promise<FetchResult<FetchResponse>>
  initialData?: FetchResponse
  queryArguments?: FetchArgument
  useFetchEffect?: boolean
  onSuccess?: (payload: FetchResponse) => void
  onError?: (e: any) => void
}

type QueryState<T = unknown> = {
  data: T
  error: string | null
  isLoading: boolean
  isCompleted: boolean
}

export function useQuery<FetchResponse = unknown, FetchArgument = unknown>(
  props: QueryProps<FetchResponse, FetchArgument>
) {
  const {
    queryMethod,
    initialData = [],
    queryArguments,
    useFetchEffect = false,
    onSuccess = null,
    onError = null,
  } = props
  const [state, dispatch] = useReducer(queryReducer, {
    isLoading: false,
    isCompleted: false,
    data: initialData as FetchResponse,
    error: null,
  })
  const [forceRefetch, setForceRefetch] = useState(false)
  const queryHandler = useCallback(
    async (arg = null): Promise<FetchResult> => {
      dispatch({ type: 'query-started' })

      const fetchProps = queryArguments || arg
      const res = await queryMethod(fetchProps)

      if (isErrorResult(res)) {
        dispatch({ type: 'query-error', payload: res.message })
        if (onError) onError(res)
        return res
      }

      dispatch({ type: 'query-success', payload: res ?? initialData })

      if (onSuccess) onSuccess(res)

      return res
    },
    [queryArguments]
  )

  useEffect(() => {
    if (useFetchEffect) {
      queryHandler()
    }
  }, [queryHandler, queryArguments, useFetchEffect])

  useEffect(() => {
    if (forceRefetch) {
      queryHandler()
    }
  }, [queryHandler, forceRefetch])

  const enableRefetch = useCallback(
    () => setForceRefetch(true),
    [setForceRefetch]
  )
  const setData = useCallback((value: FetchResponse) => {
    dispatch({ type: 'set-data', payload: value })
  }, [])
  const data = state.data as FetchResponse
  return {
    ...state,
    data,
    queryHandler,
    setData,
    forceRefetch: enableRefetch,
  }
}

type QueryReducerAction<T = unknown> =
  | { type: 'query-started' }
  | { type: 'query-success'; payload: T }
  | { type: 'set-data'; payload: T }
  | { type: 'query-error'; payload: string }

function queryReducer<T = unknown>(
  state: QueryState<T>,
  action: QueryReducerAction
) {
  switch (action.type) {
    case 'query-started':
      return { ...state, isLoading: true }
    case 'query-success':
      return {
        ...state,
        isLoading: false,
        isCompleted: true,
        data: action.payload,
      }
    case 'query-error':
      return {
        ...state,
        isLoading: false,
        isCompleted: true,
        error: action.payload,
      }
    case 'set-data':
      return { ...state, data: action.payload }
    default:
      return state
  }
}

const isErrorResult = (value: unknown): value is ErrorResult => {
  return typeof value === 'object' && 'error' in value
}
